import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Layers, Plus, Copy, Trash2, ArrowUp, ArrowDown, Star, Lock, Eye, EyeOff, Ban,
  Palette, ShieldCheck, AlertTriangle, Sparkles, Loader2, Info, LayoutDashboard, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  BAR_POSITIONS, BAR_STYLES, OVERFLOW_MODES, MOBILE_MODES, BADGE_KINDS, TAB_ICONS,
  applyTabOps, validateTabsClient, tabWidgetCounts,
} from "@/lib/dashboardTabs";
import { importDashboardAsTab } from "@/lib/tabImport";
import { FONT_FAMILIES } from "@/components/bi/FontStyleControls";
import { MediaInput } from "@/components/bi/MediaInput";
import { TabIcon } from "@/components/bi/DashboardTabs";

// Item 97 §1/§3/§8/§10/§11/§13/§16/§23 — full tab management: CRUD, ordering, formatting,
// permissions, conditional visibility, validation and AI tab operations in one place.

const Row = ({ label, children, hint }) => (
  <div className="flex items-center justify-between gap-3 py-1">
    <div className="min-w-0">
      <Label className="text-[11px] text-[color:var(--ink-2)]">{label}</Label>
      {hint && <p className="text-[10px] text-[color:var(--ink-3)]">{hint}</p>}
    </div>
    <div className="shrink-0 flex items-center gap-1.5">{children}</div>
  </div>
);

const Color = ({ value, onChange, testid, fallback = "#ffffff" }) => (
  <span className="flex items-center gap-1">
    <input data-testid={testid} type="color" value={value || fallback} onChange={(e) => onChange(e.target.value)}
      className="w-7 h-7 rounded cursor-pointer border border-[color:var(--glass-border)] bg-transparent" />
    {value ? <button onClick={() => onChange("")} className="text-[10px] text-[color:var(--ink-3)] hover:underline">reset</button> : null}
  </span>
);

const Num = ({ value, onChange, testid, min = 0, max = 200, w = "w-16" }) => (
  <Input data-testid={testid} type="number" min={min} max={max} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
    className={`h-7 ${w} text-[11px] bg-[color:var(--surface-2)]`} />
);

export function TabsManagerDialog({
  open, onOpenChange, dashboardId, tabs, widgets, bar, canEdit, cols = 12,
  onApplyOps, onBarChange, activeId, onSelect,
}) {
  const [sel, setSel] = useState(activeId || (tabs[0] || {}).id);
  const [principals, setPrincipals] = useState({ users: [], groups: [], roles: [] });
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [dashList, setDashList] = useState([]);
  const [importBusy, setImportBusy] = useState("");
  const counts = useMemo(() => tabWidgetCounts(widgets, tabs), [widgets, tabs]);
  const issues = useMemo(() => validateTabsClient(tabs, widgets), [tabs, widgets]);
  const tab = tabs.find((t) => t.id === sel) || tabs[0];

  useEffect(() => { if (open && activeId) setSel(activeId); }, [open, activeId]);
  useEffect(() => {
    if (!open || !dashboardId) return;
    api.get(`/dashboards/${dashboardId}/tabs/principals`).then(({ data }) => setPrincipals(data)).catch(() => {});
  }, [open, dashboardId]);

  const run = (ops, note) => {
    const res = applyTabOps(tabs, widgets, ops, cols);
    onApplyOps(res);
    if (note !== false && res.log.length) toast.success(res.log.join(" · "));
    if (res.activeHint) setSel(res.activeHint);
    return res;
  };
  const patch = (p) => run([{ op: "patch_tab", tab: tab.id, patch: p }], false);
  const setVis = (p) => patch({ visibility: { ...(tab.visibility || {}), ...p } });
  const setCanvasOv = (p) => patch({ canvas: { ...(tab.canvas || {}), ...p } });

  const move = (dir) => {
    const ids = tabs.map((t) => t.id);
    const i = ids.indexOf(tab.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    run([{ op: "reorder_tabs", order: ids }], false);
  };

  const askAi = async () => {
    if (!aiText.trim()) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/tab-ops", {
        instruction: aiText,
        tabs: tabs.map((t) => ({ id: t.id, name: t.name, default: t.default, hidden: t.hidden })),
        widgets: widgets.filter((w) => !w.parent_id).map((w) => ({ id: w.id, title: w.title || w.type, type: w.type, tab_id: w.tab_id })),
      });
      if (!data.ops?.length) { toast.info(data.reply || "No tab changes to make"); return; }
      const res = run(data.ops, false);
      toast.success(data.reply || res.log.join(" · "));
      setAiText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI tab assistant failed");
    } finally { setAiBusy(false); }
  };

  // Item 32: create (or re-sync) a tab from an existing dashboard
  const openImport = async () => {
    setImportOpen(true);
    if (dashList.length) return;
    try {
      const { data } = await api.get("/dashboards");
      setDashList((data || []).filter((d) => d.id !== dashboardId));
    } catch { toast.error("Could not load your dashboards"); }
  };
  const importDashboard = async (src, intoTabId = null) => {
    setImportBusy(src.id);
    try {
      const { data } = await api.get(`/dashboards/${src.id}`);
      const res = importDashboardAsTab({ tabs, widgets, source: data, tabId: intoTabId, cols });
      onApplyOps({ tabs: res.tabs, widgets: res.widgets, log: [], activeHint: res.tabId });
      setSel(res.tabId);
      setImportOpen(false);
      toast.success(`${intoTabId ? "Re-synced" : "Added"} “${data.name}” — ${res.count} widget(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not import that dashboard");
    } finally { setImportBusy(""); }
  };

  const rules = (tab?.visibility?.rules) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tabs-manager-dialog" className="max-w-3xl h-[88vh] max-h-[88vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Layers className="w-5 h-5 text-[color:var(--brand-ocean)]" />Dashboard tabs
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="tabs" className="flex-1 min-h-0 flex flex-col">
          <TabsList className="shrink-0">
            <TabsTrigger data-testid="tabsmgr-tab-tabs" value="tabs">Tabs</TabsTrigger>
            <TabsTrigger data-testid="tabsmgr-tab-format" value="format"><Palette className="w-3.5 h-3.5 mr-1" />Navigation &amp; style</TabsTrigger>
            <TabsTrigger data-testid="tabsmgr-tab-check" value="check">
              <ShieldCheck className="w-3.5 h-3.5 mr-1" />Check
              {issues.some((i) => i.severity === "error") && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-[color:var(--danger)]" />}
            </TabsTrigger>
          </TabsList>

          {/* ---------------- tabs + per-tab settings ---------------- */}
          <TabsContent value="tabs" className="flex-1 min-h-0 mt-3 overflow-hidden">
            <div className="h-full grid grid-cols-[210px_1fr] gap-3 min-h-0 overflow-hidden">
              <div className="min-h-0 flex flex-col rounded-xl border border-[color:var(--glass-border)] overflow-hidden">
                <div className="flex-1 overflow-y-auto divide-y divide-[color:var(--glass-border)]" data-testid="tabsmgr-list">
                  {tabs.map((t) => (
                    <button key={t.id} data-testid={`tabsmgr-item-${t.id}`} onClick={() => setSel(t.id)}
                      className={`w-full text-left px-2.5 py-2 flex items-center gap-1.5 text-[11px] ${t.id === sel ? "bg-[color:var(--surface-2)] text-[color:var(--ink)]" : "text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)]"}`}>
                      <TabIcon tab={t} />
                      <span className="truncate flex-1">{t.name}</span>
                      {t.default && <Star className="w-3 h-3 text-[color:var(--brand-amber)]" title="Landing tab" />}
                      {t.hidden && <EyeOff className="w-3 h-3 opacity-60" />}
                      {t.disabled && <Ban className="w-3 h-3 opacity-60" />}
                      <span className="text-[10px] text-[color:var(--ink-3)]">{counts[t.id] ?? 0}</span>
                    </button>
                  ))}
                </div>
                {canEdit && (
                  <div className="shrink-0 p-1.5 border-t border-[color:var(--glass-border)]">
                    <Button data-testid="tabsmgr-add" size="sm" variant="outline" className="w-full h-7 text-[11px]"
                      onClick={() => run([{ op: "create_tab", name: `Tab ${tabs.length + 1}` }])}>
                      <Plus className="w-3.5 h-3.5 mr-1" />Add tab
                    </Button>
                    <Button data-testid="tabsmgr-add-from-dashboard" size="sm" variant="ghost" className="w-full h-7 mt-1 text-[11px] text-[color:var(--brand-ocean)]"
                      onClick={openImport}>
                      <LayoutDashboard className="w-3.5 h-3.5 mr-1" />From existing dashboard…
                    </Button>
                  </div>
                )}
              </div>

              <div className="min-h-0 overflow-y-auto pr-1 space-y-3">
                {tab && (
                  <>
                    <div className="rounded-xl border border-[color:var(--glass-border)] p-3 space-y-1.5">
                      <Row label="Name">
                        <Input data-testid="tabsmgr-name" value={tab.name} disabled={!canEdit}
                          onChange={(e) => run([{ op: "rename_tab", tab: tab.id, name: e.target.value }], false)}
                          className="h-7 w-[200px] text-[11px] bg-[color:var(--surface-2)]" />
                      </Row>
                      <Row label="Icon">
                        <Select value={tab.icon || "__none__"} onValueChange={(v) => patch({ icon: v === "__none__" ? "" : v })}>
                          <SelectTrigger data-testid="tabsmgr-icon" className="h-7 w-[150px] text-[11px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                          <SelectContent className="max-h-[260px]">
                            <SelectItem value="__none__">No icon</SelectItem>
                            {TAB_ICONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </Row>
                      <Row label="Custom icon (SVG / PNG)" hint="Overrides the built-in icon">
                        <div className="w-[210px]"><MediaInput value={tab.icon_url} onChange={(url) => patch({ icon_url: url })} accept="image/*" testid="tabsmgr-iconurl" /></div>
                      </Row>
                      <Row label="Badge">
                        <Select value={tab.badge?.kind || "none"} onValueChange={(v) => patch({ badge: { ...(tab.badge || {}), kind: v } })}>
                          <SelectTrigger data-testid="tabsmgr-badge-kind" className="h-7 w-[110px] text-[11px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                          <SelectContent>{BADGE_KINDS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
                        </Select>
                        {tab.badge?.kind && tab.badge.kind !== "none" && tab.badge.kind !== "dot" && (
                          <Input data-testid="tabsmgr-badge-value" value={tab.badge?.value || ""} onChange={(e) => patch({ badge: { ...(tab.badge || {}), value: e.target.value } })}
                            placeholder="3" className="h-7 w-[60px] text-[11px] bg-[color:var(--surface-2)]" />
                        )}
                        {tab.badge?.kind !== "none" && <Color testid="tabsmgr-badge-color" value={tab.badge?.color} fallback="#FF6B35" onChange={(v) => patch({ badge: { ...(tab.badge || {}), color: v } })} />}
                      </Row>
                      <div className="grid grid-cols-2 gap-x-4">
                        <Row label="Landing tab"><Switch data-testid="tabsmgr-default" checked={!!tab.default} disabled={!canEdit} onCheckedChange={() => run([{ op: "set_default_tab", tab: tab.id }], false)} /></Row>
                        <Row label="Hidden"><Switch data-testid="tabsmgr-hidden" checked={!!tab.hidden} disabled={!canEdit} onCheckedChange={(v) => patch({ hidden: !!v })} /></Row>
                        <Row label="Disabled"><Switch data-testid="tabsmgr-disabled" checked={!!tab.disabled} disabled={!canEdit} onCheckedChange={(v) => patch({ disabled: !!v })} /></Row>
                        <Row label="Locked" hint="Stops layout edits on this tab"><Switch data-testid="tabsmgr-locked" checked={!!tab.locked} disabled={!canEdit} onCheckedChange={(v) => patch({ locked: !!v })} /></Row>
                      </div>
                      {tab.linked_dashboard_id && (
                        <div data-testid="tabsmgr-linked" className="flex items-center gap-2 rounded-lg bg-[color:var(--surface-2)] px-2 py-1.5">
                          <LayoutDashboard className="w-3.5 h-3.5 text-[color:var(--brand-ocean)] shrink-0" />
                          <span className="text-[10px] text-[color:var(--ink-2)] min-w-0 truncate flex-1">
                            Built from “{tab.linked_name || tab.linked_dashboard_id}”{tab.linked_at ? ` · ${new Date(tab.linked_at).toLocaleDateString()}` : ""}
                          </span>
                          {canEdit && (
                            <Button data-testid="tabsmgr-resync" size="sm" variant="outline" className="h-6 text-[10px] shrink-0"
                              disabled={!!importBusy}
                              onClick={() => importDashboard({ id: tab.linked_dashboard_id, name: tab.linked_name }, tab.id)}>
                              {importBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RefreshCw className="w-3 h-3 mr-1" />Re-sync</>}
                            </Button>
                          )}
                        </div>
                      )}
                      {canEdit && (
                        <div className="flex flex-wrap gap-1.5 pt-1.5 border-t border-[color:var(--glass-border)]">
                          <Button data-testid="tabsmgr-up" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => move(-1)}><ArrowUp className="w-3.5 h-3.5" /></Button>
                          <Button data-testid="tabsmgr-down" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => move(1)}><ArrowDown className="w-3.5 h-3.5" /></Button>
                          <Button data-testid="tabsmgr-duplicate" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => run([{ op: "duplicate_tab", tab: tab.id }])}><Copy className="w-3.5 h-3.5 mr-1" />Duplicate</Button>
                          <Select value="" onValueChange={(v) => run([{ op: "merge_tabs", from: tab.id, into: v }])}>
                            <SelectTrigger data-testid="tabsmgr-merge" className="h-7 w-[140px] px-2 text-[11px] text-[color:var(--ink-3)] bg-[color:var(--surface-2)]">
                              <span className="truncate">Merge into…</span>
                            </SelectTrigger>
                            <SelectContent>{tabs.filter((t) => t.id !== tab.id).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                          </Select>
                          <Button data-testid="tabsmgr-delete" size="sm" variant="outline" className="h-7 text-[11px] text-[color:var(--danger)]"
                            onClick={() => {
                              const n = counts[tab.id] || 0;
                              // eslint-disable-next-line no-alert
                              if (!window.confirm(`Delete "${tab.name}"${n ? ` and its ${n} widget(s)` : ""}? This cannot be undone.`)) return;
                              run([{ op: "delete_tab", tab: tab.id }]);
                            }}><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>
                          <Button data-testid="tabsmgr-open" size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => { onSelect?.(tab.id); onOpenChange(false); }}><Eye className="w-3.5 h-3.5 mr-1" />Open</Button>
                        </div>
                      )}
                    </div>

                    <div className="rounded-xl border border-[color:var(--glass-border)] p-3 space-y-1.5">
                      <div className="text-[11px] font-medium text-[color:var(--ink)] flex items-center gap-1.5"><Palette className="w-3.5 h-3.5 text-[color:var(--brand-ocean)]" />This tab's canvas</div>
                      <p className="text-[10px] text-[color:var(--ink-3)]">Overrides for this tab only — leave empty to inherit the dashboard canvas.</p>
                      <Row label="Background"><Color testid="tabsmgr-bg" value={tab.canvas?.bg_color} onChange={(v) => setCanvasOv({ bg_color: v })} /></Row>
                      <Row label="Wallpaper"><div className="w-[210px]"><MediaInput value={tab.canvas?.wallpaper_url} onChange={(url) => setCanvasOv({ wallpaper_url: url })} accept="image/*" testid="tabsmgr-wallpaper" /></div></Row>
                      <Row label="Layout mode">
                        <Select value={tab.canvas?.layout_mode || "__inherit__"} onValueChange={(v) => setCanvasOv({ layout_mode: v === "__inherit__" ? "" : v })}>
                          <SelectTrigger data-testid="tabsmgr-layoutmode" className="h-7 w-[130px] text-[11px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__inherit__">Inherit</SelectItem>
                            <SelectItem value="free">Free-form</SelectItem>
                            <SelectItem value="compact">Auto-compact</SelectItem>
                          </SelectContent>
                        </Select>
                      </Row>
                      <Row label="Grid columns">
                        <Select value={String(tab.canvas?.grid_cols || "")} onValueChange={(v) => setCanvasOv({ grid_cols: v === "__inherit__" ? "" : Number(v) })}>
                          <SelectTrigger data-testid="tabsmgr-gridcols" className="h-7 w-[110px] text-[11px] bg-[color:var(--surface-2)]"><SelectValue placeholder="Inherit" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__inherit__">Inherit</SelectItem>
                            {[12, 24, 48].map((c) => <SelectItem key={c} value={String(c)}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </Row>
                    </div>

                    <div className="rounded-xl border border-[color:var(--glass-border)] p-3 space-y-1.5">
                      <div className="text-[11px] font-medium text-[color:var(--ink)] flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[color:var(--brand-ocean)]" />Who can see this tab</div>
                      <Row label="Visibility">
                        <Select value={tab.visibility?.mode || "everyone"} onValueChange={(v) => setVis({ mode: v })}>
                          <SelectTrigger data-testid="tabsmgr-vis-mode" className="h-7 w-[170px] text-[11px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="everyone">Everyone with access</SelectItem>
                            <SelectItem value="restricted">Only chosen people</SelectItem>
                          </SelectContent>
                        </Select>
                      </Row>
                      {tab.visibility?.mode === "restricted" && (
                        <div className="space-y-2 pt-1">
                          <div>
                            <Label className="text-[10px] text-[color:var(--ink-3)]">Roles</Label>
                            <div className="flex flex-wrap gap-2 mt-1" data-testid="tabsmgr-vis-roles">
                              {(principals.roles || []).map((r) => (
                                <label key={r} className="flex items-center gap-1 text-[10px] text-[color:var(--ink-2)]">
                                  <Checkbox className="h-3 w-3" checked={(tab.visibility?.roles || []).includes(r)}
                                    onCheckedChange={(v) => setVis({ roles: v ? [...(tab.visibility?.roles || []), r] : (tab.visibility?.roles || []).filter((x) => x !== r) })} />
                                  {r.replace("customer_admin", "admin")}
                                </label>
                              ))}
                            </div>
                          </div>
                          <div>
                            <Label className="text-[10px] text-[color:var(--ink-3)]">Groups</Label>
                            <div className="flex flex-wrap gap-2 mt-1" data-testid="tabsmgr-vis-groups">
                              {(principals.groups || []).length === 0 && <span className="text-[10px] text-[color:var(--ink-3)]">No groups yet (Team → Groups)</span>}
                              {(principals.groups || []).map((g) => (
                                <label key={g.id} className="flex items-center gap-1 text-[10px] text-[color:var(--ink-2)]">
                                  <Checkbox className="h-3 w-3" checked={(tab.visibility?.group_ids || []).includes(g.id)}
                                    onCheckedChange={(v) => setVis({ group_ids: v ? [...(tab.visibility?.group_ids || []), g.id] : (tab.visibility?.group_ids || []).filter((x) => x !== g.id) })} />
                                  {g.name}
                                </label>
                              ))}
                            </div>
                          </div>
                          <div>
                            <Label className="text-[10px] text-[color:var(--ink-3)]">People</Label>
                            <Select value="" onValueChange={(v) => setVis({ user_emails: [...new Set([...(tab.visibility?.user_emails || []), v])] })}>
                              <SelectTrigger data-testid="tabsmgr-vis-user" className="h-7 mt-1 text-[11px] bg-[color:var(--surface-2)]">
                                <span className="text-[color:var(--ink-3)]">{(tab.visibility?.user_emails || []).length ? `${tab.visibility.user_emails.length} person(s)` : "Add a person…"}</span>
                              </SelectTrigger>
                              <SelectContent className="max-h-[220px]">
                                {(principals.users || []).map((u) => <SelectItem key={u.id} value={String(u.email || "").toLowerCase()}>{u.email}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {(tab.visibility?.user_emails || []).map((e) => (
                                <span key={e} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[color:var(--surface-2)] text-[color:var(--ink-2)]">
                                  {e}
                                  <button data-testid={`tabsmgr-vis-user-remove-${e}`} onClick={() => setVis({ user_emails: (tab.visibility.user_emails || []).filter((x) => x !== e) })}
                                    className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)]">×</button>
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="pt-2 border-t border-[color:var(--glass-border)]">
                        <div className="flex items-center justify-between">
                          <Label className="text-[11px] text-[color:var(--ink-2)]">Show only when…</Label>
                          <Button data-testid="tabsmgr-rule-add" size="sm" variant="outline" className="h-6 text-[10px]"
                            onClick={() => setVis({ rules: [...rules, { source: "filter", key: "", op: "eq", value: "" }] })}>
                            <Plus className="w-3 h-3 mr-1" />Rule
                          </Button>
                        </div>
                        <p className="text-[10px] text-[color:var(--ink-3)]">Conditional visibility — evaluated live against dashboard filters or what-if parameters.</p>
                        <div className="space-y-1 mt-1" data-testid="tabsmgr-rules">
                          {rules.map((r, i) => (
                            <div key={i} className="flex items-center gap-1">
                              <Select value={r.source} onValueChange={(v) => setVis({ rules: rules.map((x, j) => (j === i ? { ...x, source: v } : x)) })}>
                                <SelectTrigger data-testid={`tabsmgr-rule-source-${i}`} className="h-7 w-[86px] text-[10px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                                <SelectContent><SelectItem value="filter">Filter</SelectItem><SelectItem value="param">Parameter</SelectItem></SelectContent>
                              </Select>
                              <Input data-testid={`tabsmgr-rule-key-${i}`} value={r.key} placeholder="filter id / param"
                                onChange={(e) => setVis({ rules: rules.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)) })}
                                className="h-7 flex-1 text-[10px] bg-[color:var(--surface-2)]" />
                              <Select value={r.op} onValueChange={(v) => setVis({ rules: rules.map((x, j) => (j === i ? { ...x, op: v } : x)) })}>
                                <SelectTrigger data-testid={`tabsmgr-rule-op-${i}`} className="h-7 w-[92px] text-[10px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {["eq", "neq", "in", "contains", "gt", "gte", "lt", "lte", "set", "unset"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              <Input data-testid={`tabsmgr-rule-value-${i}`} value={r.value ?? ""} placeholder="value"
                                onChange={(e) => setVis({ rules: rules.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) })}
                                className="h-7 w-[90px] text-[10px] bg-[color:var(--surface-2)]" />
                              <button data-testid={`tabsmgr-rule-remove-${i}`} onClick={() => setVis({ rules: rules.filter((_, j) => j !== i) })}
                                className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><Trash2 className="w-3 h-3" /></button>
                            </div>
                          ))}
                          {!rules.length && <p className="text-[10px] text-[color:var(--ink-3)]">Always available.</p>}
                        </div>
                      </div>
                    </div>

                    {canEdit && (
                      <div className="rounded-xl border border-[color:var(--glass-border)] p-3 space-y-1.5">
                        <div className="text-[11px] font-medium text-[color:var(--ink)] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[color:var(--brand-mint)]" />Ask AI to organise tabs</div>
                        <div className="flex gap-1.5">
                          <Input data-testid="tabsmgr-ai-input" value={aiText} onChange={(e) => setAiText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") askAi(); }}
                            placeholder='e.g. "create a Stock tab and move Stock Aging into it"'
                            className="h-8 text-[11px] bg-[color:var(--surface-2)]" />
                          <Button data-testid="tabsmgr-ai-run" size="sm" onClick={askAi} disabled={aiBusy} className="h-8 text-[11px] bg-[color:var(--brand-mint)] text-white">
                            {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Run"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ---------------- bar formatting ---------------- */}
          <TabsContent value="format" className="flex-1 min-h-0 mt-3 overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <div className="text-[11px] font-medium text-[color:var(--ink)]">Navigation</div>
                <Row label="Show tab bar"><Switch data-testid="tabsbar-show" checked={bar.show !== false} onCheckedChange={(v) => onBarChange({ show: !!v })} /></Row>
                <Row label="Position">
                  <Select value={bar.position} onValueChange={(v) => onBarChange({ position: v })}>
                    <SelectTrigger data-testid="tabsbar-position" className="h-7 w-[140px] text-[11px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                    <SelectContent>{BAR_POSITIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Row>
                <Row label="Tab style">
                  <Select value={bar.style} onValueChange={(v) => onBarChange({ style: v })}>
                    <SelectTrigger data-testid="tabsbar-style" className="h-7 w-[140px] text-[11px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                    <SelectContent>{BAR_STYLES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Row>
                <Row label="Overflow">
                  <Select value={bar.overflow} onValueChange={(v) => onBarChange({ overflow: v })}>
                    <SelectTrigger data-testid="tabsbar-overflow" className="h-7 w-[140px] text-[11px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                    <SelectContent>{OVERFLOW_MODES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Row>
                <Row label="On phones">
                  <Select value={bar.mobile} onValueChange={(v) => onBarChange({ mobile: v })}>
                    <SelectTrigger data-testid="tabsbar-mobile" className="h-7 w-[140px] text-[11px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                    <SelectContent>{MOBILE_MODES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Row>
                <Row label="Remember tab" hint="What opens when someone returns">
                  <Select value={bar.remember} onValueChange={(v) => onBarChange({ remember: v })}>
                    <SelectTrigger data-testid="tabsbar-remember" className="h-7 w-[140px] text-[11px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="last">Last tab they used</SelectItem>
                      <SelectItem value="default">Always the landing tab</SelectItem>
                    </SelectContent>
                  </Select>
                </Row>
                <Row label="Searchable" hint="Search box appears above 4 tabs"><Switch data-testid="tabsbar-searchable" checked={bar.searchable !== false} onCheckedChange={(v) => onBarChange({ searchable: !!v })} /></Row>
                <Row label="Interactions across tabs" hint="Off = a cross-filter only affects its own tab">
                  <Switch data-testid="tabsbar-crosstab" checked={!!bar.cross_tab_interactions} onCheckedChange={(v) => onBarChange({ cross_tab_interactions: !!v })} />
                </Row>
                <Row label="Show divider"><Switch data-testid="tabsbar-divider" checked={bar.divider !== false} onCheckedChange={(v) => onBarChange({ divider: !!v })} /></Row>
                <Row label="Alignment">
                  <Select value={bar.align} onValueChange={(v) => onBarChange({ align: v })}>
                    <SelectTrigger data-testid="tabsbar-align" className="h-7 w-[110px] text-[11px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="start">Left</SelectItem><SelectItem value="center">Centre</SelectItem><SelectItem value="end">Right</SelectItem></SelectContent>
                  </Select>
                </Row>
              </div>
              <div className="space-y-1">
                <div className="text-[11px] font-medium text-[color:var(--ink)]">Container</div>
                <Row label="Bar height / width">
                  <Num testid="tabsbar-height" value={bar.height} min={24} max={96} onChange={(v) => onBarChange({ height: v })} />
                  <Num testid="tabsbar-width" value={bar.width} min={120} max={400} onChange={(v) => onBarChange({ width: v })} />
                </Row>
                <Row label="Background"><Color testid="tabsbar-bg" value={bar.bg} onChange={(v) => onBarChange({ bg: v })} /></Row>
                <Row label="Transparent"><Switch data-testid="tabsbar-transparent" checked={!!bar.transparent} onCheckedChange={(v) => onBarChange({ transparent: !!v })} /></Row>
                <Row label="Border colour"><Color testid="tabsbar-border" value={bar.border} onChange={(v) => onBarChange({ border: v })} /></Row>
                <Row label="Shadow"><Switch data-testid="tabsbar-shadow" checked={!!bar.shadow} onCheckedChange={(v) => onBarChange({ shadow: !!v })} /></Row>
                <Row label="Radius / padding / gap">
                  <Num testid="tabsbar-radius" value={bar.radius} max={32} w="w-14" onChange={(v) => onBarChange({ radius: v })} />
                  <Num testid="tabsbar-padding" value={bar.padding} max={32} w="w-14" onChange={(v) => onBarChange({ padding: v })} />
                  <Num testid="tabsbar-gap" value={bar.gap} max={32} w="w-14" onChange={(v) => onBarChange({ gap: v })} />
                </Row>
                <div className="text-[11px] font-medium text-[color:var(--ink)] pt-2">Tab items</div>
                <Row label="Font">
                  <Select value={bar.font_family || "__def__"} onValueChange={(v) => onBarChange({ font_family: v === "__def__" ? "" : v })}>
                    <SelectTrigger data-testid="tabsbar-font" className="h-7 w-[124px] text-[11px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                    <SelectContent>{FONT_FAMILIES.map((f) => <SelectItem key={f.v || "__def__"} value={f.v || "__def__"}>{f.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Row>
                <Row label="Size / weight">
                  <Num testid="tabsbar-fontsize" value={bar.font_size} min={9} max={22} w="w-14" onChange={(v) => onBarChange({ font_size: v })} />
                  <Select value={String(bar.font_weight || 500)} onValueChange={(v) => onBarChange({ font_weight: Number(v) })}>
                    <SelectTrigger data-testid="tabsbar-weight" className="h-7 w-[92px] text-[11px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                    <SelectContent>{[["400", "Regular"], ["500", "Medium"], ["600", "Semibold"], ["700", "Bold"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </Row>
                <Row label="Italic / underline">
                  <Switch data-testid="tabsbar-italic" checked={!!bar.italic} onCheckedChange={(v) => onBarChange({ italic: !!v })} />
                  <Switch data-testid="tabsbar-underline" checked={!!bar.underline} onCheckedChange={(v) => onBarChange({ underline: !!v })} />
                </Row>
                <Row label="Text / selected"><Color testid="tabsbar-text" value={bar.text} onChange={(v) => onBarChange({ text: v })} /><Color testid="tabsbar-seltext" value={bar.selected_text} onChange={(v) => onBarChange({ selected_text: v })} /></Row>
                <Row label="Hover / disabled"><Color testid="tabsbar-hovertext" value={bar.hover_text} onChange={(v) => onBarChange({ hover_text: v })} /><Color testid="tabsbar-distext" value={bar.disabled_text} onChange={(v) => onBarChange({ disabled_text: v })} /></Row>
                <Row label="Selected / hover bg"><Color testid="tabsbar-selbg" value={bar.selected_bg} onChange={(v) => onBarChange({ selected_bg: v })} /><Color testid="tabsbar-hoverbg" value={bar.hover_bg} onChange={(v) => onBarChange({ hover_bg: v })} /></Row>
                <Row label="Active indicator">
                  <Color testid="tabsbar-indicator" value={bar.indicator} onChange={(v) => onBarChange({ indicator: v })} />
                  <Num testid="tabsbar-indsize" value={bar.indicator_size} max={8} w="w-12" onChange={(v) => onBarChange({ indicator_size: v })} />
                  <Select value={bar.indicator_position} onValueChange={(v) => onBarChange({ indicator_position: v })}>
                    <SelectTrigger data-testid="tabsbar-indpos" className="h-7 w-[92px] text-[11px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                    <SelectContent>{["bottom", "top", "left", "right", "none"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </Row>
              </div>
            </div>
          </TabsContent>

          {/* ---------------- validation ---------------- */}
          <TabsContent value="check" className="flex-1 min-h-0 mt-3 overflow-y-auto pr-1">
            <div data-testid="tabsmgr-issues" className="space-y-1.5">
              {!issues.length && (
                <div className="flex items-center gap-2 text-xs text-[color:var(--brand-mint)]"><ShieldCheck className="w-4 h-4" />Every tab looks good.</div>
              )}
              {issues.map((i, n) => (
                <div key={n} data-testid={`tabsmgr-issue-${n}`} className="flex items-start gap-2 rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] px-2.5 py-2">
                  {i.severity === "error" ? <AlertTriangle className="w-3.5 h-3.5 text-[color:var(--danger)] mt-0.5" />
                    : i.severity === "warning" ? <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5" />
                    : <Info className="w-3.5 h-3.5 text-[color:var(--ink-3)] mt-0.5" />}
                  <div className="min-w-0">
                    <p className="text-[11px] text-[color:var(--ink-2)]">{i.message}</p>
                    {i.tab_id && <button onClick={() => setSel(i.tab_id)} className="text-[10px] text-[color:var(--brand-ocean)] hover:underline">Open this tab's settings</button>}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogContent data-testid="tabsmgr-import-dialog" className="max-w-md max-h-[80vh] flex flex-col">
            <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><LayoutDashboard className="w-4 h-4 text-[color:var(--brand-ocean)]" />Add a tab from a dashboard</DialogTitle></DialogHeader>
            <p className="text-[11px] text-[color:var(--ink-3)]">Its widgets, layout and canvas are copied into a new tab here. The source dashboard is not changed, and you can re-sync the tab later.</p>
            <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-[color:var(--glass-border)] rounded-lg border border-[color:var(--glass-border)] mt-2">
              {!dashList.length && <p className="p-3 text-[11px] text-[color:var(--ink-3)]">No other dashboards available.</p>}
              {dashList.map((d) => (
                <button key={d.id} data-testid={`tabsmgr-import-${d.id}`} disabled={!!importBusy}
                  onClick={() => importDashboard(d)}
                  className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-[color:var(--surface-2)] disabled:opacity-50">
                  <span className="min-w-0 flex-1">
                    <span className="block text-[12px] text-[color:var(--ink)] truncate">{d.name}</span>
                    <span className="block text-[10px] text-[color:var(--ink-3)]">{d.status || "development"}{d.category ? ` · ${d.category}` : ""}</span>
                  </span>
                  {importBusy === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[color:var(--brand-ocean)]" /> : <Plus className="w-3.5 h-3.5 text-[color:var(--ink-3)]" />}
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
